import equal from 'fast-deep-equal';
import { FormArray, FormGroup, Validators } from '@angular/forms';
import { Subject, BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { takeUntil, map, distinctUntilChanged, take } from 'rxjs/operators';
import formatISO from 'date-fns/formatISO';
import isValid from 'date-fns/isValid';
import parse from 'date-fns/parse';
import parseISO from 'date-fns/parseISO';
import { formatDate } from '@angular/common';

/**
 * deep equals 2 objects
 */
function deepEquals$1(x, y) {
    return equal(x, y);
}

/**
 * firstNotNull(null, null, 5, 6, 10) returns 5
 * @param params params of type T
 */
function firstNotNull(...params) {
    if (params == null) {
        return null;
    }
    if (params.length === 0) {
        return null;
    }
    const result = params.find(el => el != null);
    return result != null ? result : null;
}

/**
 * Handles ngOnChanges routine. Emits new event to BehaviourSubject
 * @param change SimpleChange object from ngOnChanges argument
 * @param subject BehaviourSubject<T> to emit value to
 * @param options additional options.
 * deepCompare -> perform deepEquals operation
 * valueIfNull -> if null value comes from changes, emit provided value
 * @example
 * ngOnChanges(changes: SimpleChanges): void {
 *  const searchResultsChange = changes.searchResults;
 *  if (searchResultsChange) {
 *    const searchResults = searchResultsChange.currentValue as Customer[];
 *    if (!deepEquals(searchResults, this.initialSearchResults$.getValue())) {
 *      this.initialSearchResults$.next(searchResults);
 *    }
 *  }
 *
 *  // turns into
 *  ngOnChanges(changes: SimpleChanges): void {
 *    nextOnChangeComparePrevValue(changes.searchResults, this.initialSearchResults$, { deepCompare: true });
 *  }
 */
function nextOnChangeComparePrevValue(change, subject, options = null) {
    const innerParam = Object.assign({ deepCompare: false }, (options == null ? {} : options));
    const compareFn = (oldVal, newVal) => innerParam.deepCompare ? deepEquals$1(oldVal, newVal) : oldVal === newVal;
    if (change != null) {
        let valueToNext = change.currentValue;
        if (valueToNext == null && innerParam.valueIfNull != null) {
            valueToNext = innerParam.valueIfNull;
        }
        // noinspection PointlessBooleanExpressionJS
        if (compareFn(subject.getValue(), valueToNext) === false) {
            subject.next(valueToNext);
        }
    }
}

// tslint:disable-next-line:no-any
function forEachFormGroupChild(cb) {
    Object.keys(this.controls).forEach(k => cb(this.controls[k], k));
}
// tslint:disable-next-line:no-any
function forEachFormArrayChild(cb) {
    this.controls.forEach((control, index) => {
        cb(control, index);
    });
}
/**
 * calls updateValueAndValidity() to all nested forms FormGroup or FormArray
 * @param control FormArray, FormGroup or Control
 * @param opts options
 */
function updateValueAndValidityRecursively(control, opts = { emitEvent: true }) {
    let forEachFn = null;
    if (control instanceof FormArray) {
        forEachFn = forEachFormArrayChild;
    }
    else if (control instanceof FormGroup) {
        forEachFn = forEachFormGroupChild;
    }
    if (forEachFn !== null) {
        forEachFn.call(control, (ctrl) => updateValueAndValidityRecursively(ctrl, opts));
    }
    control.updateValueAndValidity({ onlySelf: true, emitEvent: opts.emitEvent });
}

/**
 * Handles ngOnChanges routine.
 * @param change SimpleChange object from ngOnChanges argument
 * @param subject, Subject<T> to emit value to
 * @param valueIfNull if null value comes from changes, emit provided value
 * @example
 * ngOnChanges(changes: SimpleChanges): void {
 *   const lastNameChange = changes.lastNameChange;
 *   if (lastNameChange) {
 *      const lastName = lastNameChange.currentValue as string;
 *      this.lastName$.next(lastName);
 *    }
 *  }
 *
 *  // turns into:
 * ngOnChanges(changes: SimpleChanges): void {
 *   nextOnChange(changes.lastName, this.lastName$);
 * }
 */
function nextOnChange(change, subject, valueIfNull) {
    if (change == null) {
        return;
    }
    let valueToNext = change.currentValue;
    if (valueToNext == null && valueIfNull != null) {
        valueToNext = valueIfNull;
    }
    subject.next(valueToNext);
}

/**
 * checks if a param is null or undefined
 */
function isNil(obj) {
    return obj === null || obj === undefined;
}
/**
 * checks if input value is a string
 */
function isString(str) {
    return typeof str === 'string';
}
/**
 * checks if an input parameter is null or empty string
 */
function isNullOrEmpty(str) {
    return isNil(str) || (isString(str) && str === '');
}
/**
 * check if input parameter is null or empty string or a string containing special characters (whitespaces e.g. 'tab')
 */
function isNullOrWhiteSpace(str) {
    if (str == null) {
        return true;
    }
    return isString(str) && str.trim() === '';
}
/**
 * Return a provided value if the input arg is not a string
 * @param arg any js value
 * @param ifNotStringValue value to return if arg is not a string
 */
function ifNotString(arg, ifNotStringValue = '') {
    return (typeof arg === 'string') ? arg : ifNotStringValue;
}
/**
 * Removes all characters from string except digits
 * @param str input string
 * @example
 * const digits = removeNonDigits('a+98--RR'); // => '98'
 */
function removeNonDigits(str) {
    return (str !== null && str !== void 0 ? str : '').replace(/\D/g, '');
}
/**
 * Check input value is convertible to number
 * @param value value to check
 */
// https://github.com/datorama/akita/blob/721243c814b8835a0bec113a686c5a1400dc2f2a/libs/akita/src/lib/isNumber.ts
function isNumber(value) {
    return !isArray(value) && value - parseFloat(value) + 1 >= 0;
}
/**
 * Check input value is array
 * @param value value to check
 */
function isArray(value) {
    return Array.isArray(value);
}
/**
 * Converts a valid number to number, any other value to 0
 * @param val value to convert
 */
function toNumber(val) {
    if (isNumber(val)) {
        return parseFloat(val);
    }
    return 0;
}
/**
 * Checks that value is a plain js object, e.g. { a: 0, b: 'Ivan' }
 * @param value value to check
 */
function isPlainObject(value) {
    return _toBoolean(value) && value.constructor.name === 'Object';
}
function _toBoolean(value) {
    return value != null && `${value}` !== 'false';
}

/**
 * works like selectMany in LINQ
 * @param input input collection
 * @param selectListFn collection to select
 * @example
 * const testArray = [
 * {name: 'Ivan', values: [1, 2] },
 * {name: 'Petr', values: [1, 2, 3] }
 * ];
 *
 * const result = selectMany(testArray, x => x.values); // [1, 2, 1, 2, 3]
 */
function selectMany(input, selectListFn) {
    return input.reduce((out, inx) => {
        out.push(...selectListFn(inx));
        return out;
    }, new Array());
}

/**
 * coalesce(null, null, 5, 6, 10) returns 5
 * @param params params of type T
 */
function coalesce(...params) {
    if (params == null) {
        return null;
    }
    if (params.length === 0) {
        return null;
    }
    const result = params.find(el => el != null);
    return result != null ? result : null;
}

/**
 * Utility class to work with object properties
 */
class FFObject {
    /**
     * Get the collection of key of a plain object, throws error if not an object
     * @param arg any argument of a plain object
     * @example
     * const aaa = { a: 'text', b: 2 };
     * const keys = FFObject.keys(aaa); // ['a', 'b']
     */
    static keys(arg) {
        if (arg == null) {
            return [];
        }
        if (!isPlainObject(arg)) {
            throw new Error('FFObject.keys accepts only plain js objects');
        }
        return Object.keys(arg);
    }
}

function deepEquals(x, y) {
    return equal(x, y);
}
/**
 * Plugin that helps monitor store changes
 * @example
 * const nameEmitter$ = new BehaviourSubject<{name: string}>({ name: 'FirstValue' });
 * const checker = new DirtyChecker<{name: string}>(nameEmitter$);
 * checker.isDirty$$.subscribe(console.log);
 * // console => false
 * nameEmitter$.next({ name: 'SecondValue' });
 * // console => true
 * checker.saveSnapshot(); // save last value as new master value to compare
 * // console => false;
 * nameEmitter$.next({ name: 'SecondValue' });
 * // no console output, because nothing changed
 * nameEmitter$.next({ name: 'Bla-bla' });
 * // console => true
 * checker.destroy(); // do not forget to destroy the object
 */
class DirtyChecker {
    /**
     * @param snapshot any query to the store
     */
    constructor(snapshot) {
        // for takeUntil
        this._destroy$ = new Subject();
        // stores current value when saveSnapshot() is called
        this._savedSnapshot$ = new BehaviorSubject(null);
        // holds current state value that comes from the constructor. We use separate behavior subject to ensure
        // we always have some value and don't need to wait for the constructor observable ot emit
        this._currentState$ = new BehaviorSubject(null);
        // holds the calculated value of dirty check to speed up the plugin
        this._isDirtyState$ = new BehaviorSubject(true);
        /**
         * Observable that holds current state of the dirty check
         */
        this.isDirty$$ = this._isDirtyState$.asObservable();
        if (snapshot == null) {
            throw new Error('constructor parameter should not be null');
        }
        // subscribe update current state
        snapshot.pipe(takeUntil(this._destroy$)).subscribe((data) => this._currentState$.next(data));
        // save first snapshot
        this.saveSnapshot();
        // subscribe 'dirty' calculated value
        combineLatest([this._currentState$, this._savedSnapshot$]).pipe(map(([currState, savedSnapshot]) => ({ currState, savedSnapshot: JSON.parse(savedSnapshot !== null && savedSnapshot !== void 0 ? savedSnapshot : 'null') })), map(data => !deepEquals(data.currState, data.savedSnapshot)), distinctUntilChanged(), takeUntil(this._destroy$)).subscribe(is => this._isDirtyState$.next(is));
    }
    /**
     * Saves current snapshot for the further comparison
     */
    saveSnapshot() {
        var _a;
        this._savedSnapshot$.next(JSON.stringify((_a = this._currentState$.getValue()) !== null && _a !== void 0 ? _a : null));
    }
    /**
     * Function to check whether the state is dirty on its call
     */
    isDirty() {
        return this._isDirtyState$.getValue();
    }
    destroy() {
        this._destroy$.next(true);
        this._destroy$.complete();
    }
}

// https://stackoverflow.com/a/16245768
function convertBase64ToBlob(b64Data, contentType = '', sliceSize = 512) {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        // @ts-ignore
        byteArrays.push(byteArray);
    }
    // @ts-ignore
    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
}

function convertBlobToBase64(blob) {
    const resultObservable = new Observable(obs => {
        if (blob == null) {
            obs.next(null);
            obs.complete();
            return;
        }
        const fileReader = new FileReader();
        fileReader.onloadend = () => {
            const base64String = ('' + fileReader.result).replace(/^data:.+;base64,/, '');
            obs.next(base64String);
            obs.complete();
        };
        fileReader.onerror = () => {
            obs.error('Error parsing blob');
            obs.complete();
        };
        fileReader.onabort = () => {
            obs.complete();
        };
        fileReader.readAsDataURL(blob);
    });
    return resultObservable;
}

/**
 * Converts base64 string to File
 * @param base64 any string
 * @param fileName name of file
 * @param extension extension of file. Optional
 */
function convertBase64ToFile(base64, fileName, extension) {
    const attachBlob = convertBase64ToBlob(base64 !== null && base64 !== void 0 ? base64 : '', `image/${extension}`);
    // lastModified is necessary for proper conversion. We use the current date, because this parameter does not affect further use
    return new File([attachBlob], fileName !== null && fileName !== void 0 ? fileName : '', {
        type: extension != null ? `image/${extension}` : '',
        lastModified: new Date().getMilliseconds()
    });
}

/**
 * Generates a string with random characters. E.g. '1zd9so9q3f6'.
 */
function randomString() {
    return (Number(String(Math.random()).slice(2)) +
        Date.now() +
        Math.round(performance.now())).toString(36);
}

/**
 * Converts any input string to iso date (2020-02-20T00:00:00)
 * @param date date in any format
 */
function toIsoString(date) {
    if (date == null) {
        throw new Error('data is null, can\'t convert');
    }
    // check for moment object
    if (_isMoment(date)) {
        const momentObj = date;
        return momentObj.toISOString().substring(0, 10) + 'T00:00:00';
    }
    // check for luxon object
    if (_isLuxon(date)) {
        const luxonObj = date;
        return luxonObj.toISO().substring(0, 10) + 'T00:00:00';
    }
    if (_isJsDate(date)) {
        if (isValid(date) === false) {
            throw new Error('Date is not valid');
        }
        return formatISO(date, { representation: 'date' }) + 'T00:00:00';
    }
    if (_isStringIsoDate(date)) {
        const trimmed = date.substring(0, 10);
        return formatISO(parse(trimmed, 'yyyy-MM-dd', new Date(1970, 0, 1)), { representation: 'date' }) + 'T00:00:00';
    }
    if (_isStringddMMyyyDate(date, '.')) {
        const trimmed = date.substring(0, 10);
        return formatISO(parse(trimmed, 'dd.MM.yyyy', new Date(1970, 0, 1)), { representation: 'date' }) + 'T00:00:00';
    }
    if (_isStringddMMyyyDate(date, '/')) {
        const trimmed = date.substring(0, 10);
        return formatISO(parse(trimmed, 'dd/MM/yyyy', new Date(1970, 0, 1)), { representation: 'date' }) + 'T00:00:00';
    }
    throw new Error('Could not covert date ' + JSON.stringify(date));
}
/**
 * Converts any input value to iso date with time (2020-02-20T15:16:17)
 * @param date date in any format
 */
function toISODateTimeString(date) {
    if (date == null) {
        throw new Error('data is null, can\'t convert');
    }
    // check for moment object
    if (_isMoment(date)) {
        const momentObj = date;
        return momentObj.format('YYYY-MM-DDTHH:mm:ss');
    }
    // check for luxon object
    if (_isLuxon(date)) {
        const luxonObj = date;
        return luxonObj.toISO({ includeOffset: false, suppressMilliseconds: true });
    }
    if (_isJsDate(date)) {
        if (isValid(date) === false) {
            throw new Error('Date is not valid');
        }
        return formatISO(date).substring(0, 19);
    }
    if (_isStringIsoDateTime(date)) {
        const trimmed = date.substring(0, 19);
        return formatISO(parseISO(trimmed)).substring(0, 19);
    }
    throw new Error('Could not covert date ' + JSON.stringify(date));
}
/**
 * Converts any input value to formatted date (dd/MM/yyyy)
 * @param date date in any format
 */
function toFormatDateString(date) {
    if (date == null) {
        throw new Error('data is null, can\'t convert');
    }
    // check for moment object
    if (_isMoment(date)) {
        const momentObj = date;
        return momentObj.format('DD/MM/YYYY');
    }
    // check for luxon object
    if (_isLuxon(date)) {
        const luxonObj = date;
        return luxonObj.toFormat('dd/MM/yyyy');
    }
    if (_isJsDate(date)) {
        if (isValid(date) === false) {
            throw new Error('Date is not valid');
        }
        return formatDate(date, 'dd/MM/yyyy', 'en');
    }
    if (_isStringIsoDateTime(date)) {
        const trimmed = date.substring(0, 19);
        return formatDate(parseISO(trimmed), 'dd/MM/yyyy', 'en');
    }
    throw new Error('Could not covert date ' + JSON.stringify(date));
}
/**
 * checks value is a parsable date (not date with time)
 * @param date in any format
 */
function isDate(date) {
    return date != null && (_isMoment(date) || _isLuxon(date) || _isJsDate(date) || _isStringIsoDate(date)
        || _isStringddMMyyyDate(date, '.') || _isStringddMMyyyDate(date, '/'));
}
function _isMoment(date) {
    var _a;
    if (date == null || typeof date !== 'object') {
        return false;
    }
    return (_a = ((date === null || date === void 0 ? void 0 : date.hasOwnProperty('_isAMomentObject')) && date.isValid())) !== null && _a !== void 0 ? _a : false;
}
function _isLuxon(date) {
    var _a;
    if (date == null || typeof date !== 'object') {
        return false;
    }
    return (_a = ((date === null || date === void 0 ? void 0 : date.hasOwnProperty('isLuxonDateTime')) && !!Object.keys(date).find(key => key === 'isLuxonDateTime')
        && date.isValid)) !== null && _a !== void 0 ? _a : false;
}
function _isJsDate(date) {
    return date != null && date instanceof Date && !isNaN(date.getTime());
}
function _isStringIsoDate(date) {
    if (date != null && typeof date === 'string') {
        if (date.length < 10) {
            return false;
        }
        const trimmed = date.substring(0, 10);
        const regExp = /^\d{4}-\d{2}-\d{2}$/g;
        if (regExp.test(trimmed)) {
            const p = parse(trimmed, 'yyyy-MM-dd', new Date(1970, 0, 1));
            return isValid(p);
        }
    }
    return false;
}
function _isStringIsoDateTime(date) {
    if (date != null && typeof date === 'string') {
        if (date.length < 19) {
            return false;
        }
        const trimmed = date.substring(0, 19);
        const regExp = /^[0-9]{4}-((0[13578]|1[02])-(0[1-9]|[12][0-9]|3[01])|(0[469]|11)-(0[1-9]|[12][0-9]|30)|(02)-(0[1-9]|[12][0-9]))T(0[0-9]|1[0-9]|2[0-3]):(0[0-9]|[1-5][0-9]):(0[0-9]|[1-5][0-9])(\.[0-9]{,3}Z)?$/;
        if (regExp.test(trimmed)) {
            const p = parseISO(trimmed);
            return isValid(p);
        }
    }
    return false;
}
/**
 * checks if date is in these formats dd.MM.yyyy, dd-MM-yyyy, dd/MM/yyyy, dd,MM,yyyy
 * @param date date in any format
 * @param separator separator symbol between date parts
 */
function _isStringddMMyyyDate(date, separator) {
    if (date == null || typeof date !== 'string') {
        return false;
    }
    if (date.length < 10) {
        return false;
    }
    const trimmed = date.substring(0, 10);
    const dt = parse(trimmed, `dd${separator}MM${separator}yyyy`, 0);
    return isValid(dt);
}

/**
 * Additional validators
 */
class FFValidators {
    // tslint:disable-next-line:max-line-length
    static isDateNotLaterThan(value, compareDate) {
        const valueDate = isDate(value) ? toIsoString(value) : null;
        const compareDateDate = (typeof compareDate === 'function') ? compareDate() : compareDate;
        const compareDateIso = isDate(compareDateDate) ? toIsoString(compareDateDate) : null;
        if (valueDate == null || compareDateIso == null) {
            return true;
        }
        return valueDate <= compareDateIso;
    }
    /**
     * Error { dateNotLaterThan: 'yyyy-MM-ddT00:00:00' }, where 'yyyy-MM-ddT00:00:00' is compareDate
     */
    // tslint:disable-next-line:max-line-length
    static dateNotLaterThan(compareDate) {
        // noinspection UnnecessaryLocalVariableJS
        const result = (control) => {
            if (this.isDateNotLaterThan(control.value, compareDate)) {
                return null;
            }
            const compareDateArgValue = (typeof compareDate === 'function') ? compareDate() : compareDate;
            const compareDateDate = isDate(compareDateArgValue) ? toFormatDateString(compareDateArgValue) : null;
            return compareDateDate == null ? null : { dateNotLaterThan: compareDateDate };
        };
        return result;
    }
    /**
     * Error { dateNotLaterThan: 'yyyy-MM-ddT00:00:00' }, where 'yyyy-MM-ddT00:00:00' is compareDate
     */
    // tslint:disable-next-line:max-line-length
    static dateNotLaterThanAsync(compareDate$) {
        // noinspection UnnecessaryLocalVariableJS
        const result = (control) => {
            return compareDate$.pipe(take(1), map(compareDate => this.dateNotLaterThan(compareDate)(control)));
        };
        return result;
    }
    // tslint:disable-next-line:max-line-length
    static isDateNotEarlierThan(value, compareDate) {
        const valueDate = isDate(value) ? toIsoString(value) : null;
        const compareDateDate = (typeof compareDate === 'function') ? compareDate() : compareDate;
        const compareDateIso = isDate(compareDateDate) ? toIsoString(compareDateDate) : null;
        if (valueDate == null || compareDateIso == null) {
            return true;
        }
        return valueDate >= compareDateIso;
    }
    /**
     * Error { dateNotEarlierThan: 'yyyy-MM-ddT00:00:00' }, where 'yyyy-MM-ddT00:00:00' is compareDate
     */
    // tslint:disable-next-line:max-line-length
    static dateNotEarlierThan(compareDate) {
        // noinspection UnnecessaryLocalVariableJS
        const result = (control) => {
            if (this.isDateNotEarlierThan(control.value, compareDate)) {
                return null;
            }
            const compareDateArgValue = (typeof compareDate === 'function') ? compareDate() : compareDate;
            const compareDateDate = isDate(compareDateArgValue) ? toFormatDateString(compareDateArgValue) : null;
            return compareDateDate == null ? null : { dateNotEarlierThan: compareDateDate };
        };
        return result;
    }
    /**
     * Error { dateNotEarlierThan: 'yyyy-MM-ddT00:00:00' }, where 'yyyy-MM-ddT00:00:00' is compareDate
     */
    // tslint:disable-next-line:max-line-length
    static dateNotEarlierThanAsync(compareDate$) {
        // noinspection UnnecessaryLocalVariableJS
        const result = (control) => {
            return compareDate$.pipe(take(1), map(compareDate => this.dateNotEarlierThan(compareDate)(control)));
        };
        return result;
    }
    static isEmail(email) {
        if (isNullOrEmpty(email)) {
            return true;
        }
        const regex = /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/gm;
        return regex.test(email);
    }
    /**
     * Boolean function for Validators.required
     * @param value value to validate
     */
    static isRequired(value) {
        return Validators.required({ value }) == null;
    }
    /**
     * Boolean function for checking that input value is not whitespace
     * @param value value to check
     * @return true -> validator is okay (no error),  false - error
     */
    static isRequiredNoWhitespaces(value) {
        // if no value, then return false (error)
        if (value == null) {
            return false;
        }
        // if value is not string, then the validation seems to be logically okay, as the object can't contain whitespaces
        // noinspection RedundantIfStatementJS
        if (isString(value) && isNullOrWhiteSpace(value)) {
            return false;
        }
        return true;
    }
    /**
     * Boolean function for Validators.pattern
     * @param pattern regexp to validate against
     * @param value value to validate
     */
    static isPattern(pattern, value) {
        return Validators.pattern(pattern)({ value }) == null;
    }
    /**
     * Boolean function for requiredTrue
     */
    static isRequiredTrue(value) {
        return Validators.requiredTrue({ value }) == null;
    }
    /**
     * Boolean function for Validators.minLength
     */
    static isMinLength(minLength, value) {
        return Validators.minLength(minLength)({ value }) == null;
    }
    /**
     * Boolean function for Validators.maxLength
     */
    static isMaxLength(maxLength, value) {
        return Validators.maxLength(maxLength)({ value }) == null;
    }
    /**
     * Boolean function for passwordMatchValidator
     */
    // tslint:disable-next-line:max-line-length
    static isPasswordMatch(value, comparePassword) {
        const compare = (typeof comparePassword === 'function') ? comparePassword() : comparePassword;
        // if no value was specified, then also assume the validation is correct
        if ((value !== null && value !== void 0 ? value : '') === '') {
            return true;
        }
        return (value !== null && value !== void 0 ? value : '') === (compare !== null && compare !== void 0 ? compare : '');
    }
    /**
     * Error { noPasswordMatch: true } if comparePassword doesn't match password
     */
    static passwordMatch(comparePassword) {
        // noinspection UnnecessaryLocalVariableJS
        const result = (control) => {
            if (this.isPasswordMatch(control.value, comparePassword)) {
                return null;
            }
            return { noPasswordMatch: true };
        };
        return result;
    }
    /**
     * Error { noPasswordMatch: true } if comparePassword$ doesn't match password
     */
    // tslint:disable-next-line:max-line-length
    static passwordMatchAsync(comparePassword$) {
        // noinspection UnnecessaryLocalVariableJS
        const result = (control) => {
            return comparePassword$.pipe(take(1), map(comparePassword => this.passwordMatch(comparePassword)(control)));
        };
        return result;
    }
    /**
     * Boolean function for passwordStrongEnough
     */
    static isPasswordStrongEnough(password) {
        if (isNullOrEmpty(password)) {
            return true;
        }
        const regex = new RegExp('^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$');
        return regex.test(password);
    }
    static _getEmail() {
        // noinspection UnnecessaryLocalVariableJS
        const result = (control) => FFValidators.isEmail(control.value) ? null : { email: true };
        return result;
    }
    static _getPasswordStrongEnough() {
        // noinspection UnnecessaryLocalVariableJS
        const result = (control) => FFValidators.isPasswordStrongEnough(control.value) ? null : { passwordWeak: true };
        return result;
    }
    static _getPasswordStrongEnoughAsync() {
        // noinspection UnnecessaryLocalVariableJS
        const result = (control) => {
            return of(0).pipe(map(() => FFValidators.passwordStrongEnough(control)));
        };
        return result;
    }
    static _getRequiredNoWhitespaces() {
        // noinspection UnnecessaryLocalVariableJS
        const result = (control) => {
            return FFValidators.isRequiredNoWhitespaces(control.value) ? null : { required: true };
        };
        return result;
    }
}
/**
 * Error: { email: true }
 */
FFValidators.email = FFValidators._getEmail();
/**
 * Error { required: true } even if a field contains tabs or spaces
 */
FFValidators.requiredNoWhitespaces = FFValidators._getRequiredNoWhitespaces();
/**
 * Error: { passwordWeak: true }
 */
FFValidators.passwordStrongEnough = FFValidators._getPasswordStrongEnough();
FFValidators.passwordStrongEnoughAsync = FFValidators._getPasswordStrongEnoughAsync();

/*
 * Public API Surface of utils
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DirtyChecker, FFObject, FFValidators, coalesce, convertBase64ToBlob, convertBase64ToFile, convertBlobToBase64, deepEquals$1 as deepEquals, firstNotNull, ifNotString, isArray, isDate, isNil, isNullOrEmpty, isNullOrWhiteSpace, isNumber, isPlainObject, isString, nextOnChange, nextOnChangeComparePrevValue, randomString, removeNonDigits, selectMany, toFormatDateString, toISODateTimeString, toIsoString, toNumber, updateValueAndValidityRecursively };
